/**
 * Есть ли в формах настроек несохранённые правки (стори 18.4).
 *
 * <p>Раньше окно закрывалось молча — кнопкой «Отмена» под паролем, крестиком, Escape в вебе,
 * аппаратной «назад» на Android, — и всё напечатанное просто исчезало. Ни предупреждения, ни
 * следа: человек был уверен, что сохранил.
 *
 * <p>Правило вынесено сюда, а не написано по месту, ровно по одной причине: компонентных тестов
 * в проекте нет, и проверить «что считается несохранённым» иначе можно было бы только руками на
 * устройстве. Сравнение идёт с сохранённым профилем, а не с моментом открытия окна: вернуть
 * поле к исходному значению — это не правка.
 */

import { formatBirthDateInput } from '../../util/birthdays';

export type SettingsDraft = {
  login: string;
  displayName: string;
  statusText: string;
  bio: string;
  publicHandle: string;
  currentPassword: string;
  newPassword: string;
  /** Пол; `null` — «не указан», и это такое же значение, как остальные. */
  gender: string | null;
  /** Дата рождения как её печатают — ДД.ММ.ГГГГ, а не ISO из профиля. */
  birthDate: string;
};

export type SavedSettings = {
  login: string | null | undefined;
  displayName: string | null | undefined;
  statusText: string | null | undefined;
  bio: string | null | undefined;
  publicHandle: string | null | undefined;
  gender: string | null | undefined;
  /** Из профиля приходит ISO `yyyy-MM-dd`; приводится к виду поля перед сравнением. */
  birthDate: string | null | undefined;
};

function norm(value: string | null | undefined): string {
  return (value ?? '').trim();
}

/**
 * Ник сравнивается без регистра: сервер хранит его в нижнем, и «Zorro» против `zorro` —
 * не правка, а та же строка в другом написании.
 */
function normHandle(value: string | null | undefined): string {
  return norm(value).toLowerCase();
}

/**
 * Разделы окна, у которых есть несохранённые правки, — в порядке сохранения кнопкой «Готово»
 * (аудит удобства 2026-10-04, п. 38). Каждый раздел — своя серверная ручка, поэтому «Готово»
 * сохраняет их по очереди и останавливается на первой ошибке.
 *
 * <p>Порядок не случаен: профиль раньше логина, потому что оба пишут один и тот же
 * `PATCH /api/users/me` целиком, и логин должен уйти уже с новым именем, а не затереть его
 * старым.
 */
export type SettingsSection = 'profile' | 'login' | 'handle' | 'password';

export function dirtySettingsSections(draft: SettingsDraft, saved: SavedSettings): SettingsSection[] {
  const out: SettingsSection[] = [];
  if (
    norm(draft.displayName) !== norm(saved.displayName) ||
    norm(draft.statusText) !== norm(saved.statusText) ||
    norm(draft.bio) !== norm(saved.bio) ||
    norm(draft.gender) !== norm(saved.gender) ||
    // Дата сравнивается в виде поля, а не ISO: иначе «01.02.1990» против `1990-02-01` всегда
    // читалось бы как правка, и окно спрашивало бы про потерю набранного при каждом закрытии.
    norm(draft.birthDate) !== formatBirthDateInput(saved.birthDate)
  ) {
    out.push('profile');
  }
  if (norm(draft.login) !== norm(saved.login)) out.push('login');
  if (normHandle(draft.publicHandle) !== normHandle(saved.publicHandle)) out.push('handle');
  // Пароль: начатый ввод — тоже несохранённое. Полей два, и достаточно любого: человек,
  // напечатавший текущий пароль и закрывший окно, пароль не менял, но и терять набранное молча
  // мы не должны.
  if (norm(draft.currentPassword).length > 0 || norm(draft.newPassword).length > 0) out.push('password');
  return out;
}

export function hasUnsavedSettings(draft: SettingsDraft, saved: SavedSettings): boolean {
  return dirtySettingsSections(draft, saved).length > 0;
}
